import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { customerService } from '../../../services/api';
import { getDepartmentName, useDepartments } from '../../../hooks/useDepartments';
import { formatDate } from '../../metrics/utils';
import { campaignLabel } from '../../campaigns/campaignLabel';

/**
 * Filtros de leads que qualquer tela pode mandar pela URL para a lista de
 * leads (`/dashboard/leads?temperature=hot&service=handoff_unattended`). Cada
 * filtro é um parâmetro da API; listas vão separadas por vírgula. Mesmos
 * segmentos da jornada (backend: services/lead_segments.py).
 */
export const LEAD_PARAMS = [
  'entry',
  'department',
  'temperature',
  'service',
  'collaborator',
  'stage',
  'assigned_to_id__in',
  'business_id__in',
  'status',
  'created_at__gte',
  'created_at__lt',
  'contact',
];

const split = (value) => value.split(',').filter(Boolean);

/** Parâmetros conhecidos da URL → objeto de parâmetros da API. */
export const leadParamsFromSearch = (searchParams) => Object.fromEntries(
  LEAD_PARAMS.filter((name) => searchParams.get(name)).map((name) => [name, searchParams.get(name)]),
);

/** Objeto de parâmetros → query string para link (`?a=b&c=d`). */
export const leadParamsToSearch = (params) => {
  const search = new URLSearchParams(
    Object.entries(params).filter(([, value]) => value != null && value !== ''),
  ).toString();
  return search ? `?${search}` : '';
};

// Nomes para os chips: listas que já existem na API, buscadas só quando o
// filtro correspondente está na URL.
const useNames = (params) => {
  const { data: departments } = useDepartments();
  const needs = (name) => Boolean(params[name]);

  const { data: collaborators = [] } = useQuery({
    queryKey: ['collaborators', 'names'],
    queryFn: () => customerService
      .getCollaborators({ paginationModel: { page: 0, pageSize: 500 } })
      .then((res) => res.data?.results ?? res.data ?? []),
    enabled: needs('collaborator') || needs('assigned_to_id__in'),
    staleTime: 5 * 60 * 1000,
  });
  const { data: origins = [] } = useQuery({
    queryKey: ['lead-origins', 'names'],
    queryFn: () => customerService.getLeadOrigins().then((res) => res.data?.results ?? res.data ?? []),
    enabled: needs('entry') && params.entry.includes('origin:'),
    staleTime: 5 * 60 * 1000,
  });
  const campaignIds = needs('entry')
    ? split(params.entry).filter((key) => key.startsWith('campaign:')).map((key) => key.slice(9))
    : [];
  const { data: campaigns = [] } = useQuery({
    queryKey: ['campaigns', 'names', campaignIds],
    queryFn: () => Promise.all(campaignIds.map((uuid) => customerService.getCampaign(uuid).then((res) => res.data))),
    enabled: campaignIds.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  const { data: contact } = useQuery({
    queryKey: ['contact', params.contact],
    queryFn: () => customerService.getContact(params.contact).then((res) => res.data),
    enabled: needs('contact'),
    staleTime: 5 * 60 * 1000,
  });

  const { data: stores = [] } = useQuery({
    queryKey: ['businesses', 'names'],
    queryFn: () => customerService.listBusinesses().then((res) => res.data?.results ?? res.data ?? []),
    enabled: needs('business_id__in'),
    staleTime: 5 * 60 * 1000,
  });

  return { departments, collaborators, origins, campaigns, stores, contact };
};

/**
 * Chips dos filtros ativos (rótulo legível + remover), para o
 * ActiveFiltersBar. Descrevem os filtros, não a tela de onde veio o link.
 */
export const useLeadParamChips = (params, onRemove) => {
  const { t } = useTranslation();
  const names = useNames(params);

  const valueLabel = (name, value) => {
    switch (name) {
      case 'entry': {
        if (value === 'organic') return t('metrics.journey.entry.organic');
        const [kind, uuid] = value.split(':');
        if (kind === 'campaign') {
          const campaign = names.campaigns.find((item) => item?.uuid === uuid);
          return campaign ? campaignLabel(campaign) : t('leads.params.campaign');
        }
        return names.origins.find((item) => item.uuid === uuid)?.name ?? t('leads.params.origin');
      }
      case 'department':
        return value === 'none' ? t('metrics.journey.department.none') : getDepartmentName(names.departments, value);
      case 'temperature':
        return t(`metrics.journey.temperature.${value}`);
      case 'service':
        return t(`metrics.journey.service.${value}`);
      case 'collaborator':
      case 'assigned_to_id__in': {
        const collaborator = names.collaborators.find((item) => item.uuid === value);
        return collaborator?.name || t('leads.params.collaborator');
      }
      case 'stage':
        return t(`leads.board.columns.${value}`);
      case 'business_id__in': {
        const store = names.stores.find((item) => item.uuid === value);
        return store?.display_name || store?.name || t('leads.params.business_id__in');
      }
      case 'status':
        return t(`metrics.status.${value}`, { defaultValue: value });
      case 'contact':
        return names.contact?.display_name || t('leads.params.contact');
      case 'created_at__gte':
        return formatDate(value);
      case 'created_at__lt':
        // Fim exclusivo: o link usa o dia seguinte ao último dia do período.
        return formatDate(shiftDate(value, -1));
      default:
        return value;
    }
  };

  return Object.entries(params).map(([name, value]) => ({
    key: name,
    label: `${t(`leads.params.${name}`)}: ${split(value).map((item) => valueLabel(name, item)).join(', ')}`,
    onDelete: () => onRemove(name),
  }));
};

export const shiftDate = (isoDate, days) => {
  const [year, month, day] = isoDate.split('-').map(Number);
  const date = new Date(year, month - 1, day + days);
  const pad = (value) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};
